import React, { useEffect, useState, useCallback } from 'react';
import { getEarnOverview, subscribeEarnFlexible, redeemEarnFlexible, getEarnAutoConfig, setEarnAutoConfig } from '../../services/EarnService';

// Painel de Rendimento do Caixa (Simple Earn). Fase 1b (leitura) + Fase 2 (escrita FLEXÍVEL, manual-confirm).
// Mostra AO VIVO da Binance: posição (quanto rende + juros) + produtos (APR/liquidez/lock/mín). Aplicar/Resgatar
// SÓ no flexível (líquido, resgate ~imediato), sempre com confirmação explícita. Earn é mainnet-only → fail-open.
const pct = a => `${((Number(a) || 0) * 100).toFixed(2)}%`;
const fmt = n => (Number(n) || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
const ago = ts => {
    const s = Math.max(0, Math.round((Date.now() - Number(ts)) / 1000));
    if (s < 60) return 'há instantes';
    const m = Math.round(s / 60);
    if (m < 60) return `há ${m} min`;
    const h = Math.round(m / 60);
    return h < 24 ? `há ${h}h` : `há ${Math.round(h / 24)}d`;
};

function friendlyReason(reason) {
    if (!reason) return 'indisponível neste ambiente.';
    if (reason === 'NO_PROD_CREDENTIALS') return 'configure as credenciais de PRODUÇÃO da Binance (Earn não existe em testnet).';
    if (/-2008|invalid.?api-?key|api-?key/i.test(reason)) return 'a chave de produção é inválida ou sem permissão de leitura de Earn.';
    return reason;
}

function EarnPanel({ asset = 'USDT' }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState(null); // { mode:'subscribe'|'redeem', productId, apr, min }
    const [amountInput, setAmountInput] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null); // { ok, text }
    const [auto, setAuto] = useState(null); // { enabled, keepFreeUSDT, preview }
    const [keepInput, setKeepInput] = useState('');
    const [savingAuto, setSavingAuto] = useState(false);
    const [syncSecs, setSyncSecs] = useState(0); // countdown pós-operação: Binance leva alguns segundos pra refletir

    const reload = useCallback(() => {
        return getEarnOverview(asset)
            .then(d => { setData(d); setLoading(false); })
            .catch(() => setLoading(false));
    }, [asset]);

    const loadAuto = useCallback(() => {
        return getEarnAutoConfig()
            .then(c => { setAuto(c); setKeepInput(c.keepFreeUSDT != null ? String(c.keepFreeUSDT) : ''); })
            .catch(() => { });
    }, []);

    useEffect(() => { let alive = true; reload().finally(() => { if (!alive) return; }); return () => { alive = false; }; }, [reload]);
    useEffect(() => { loadAuto(); }, [loadAuto]);

    // Poll leve enquanto o painel está aberto: reflete o que o auto-keeper move sozinho (server-side) sem F5.
    useEffect(() => {
        const id = setInterval(() => { reload(); loadAuto(); }, 30000);
        return () => clearInterval(id);
    }, [reload, loadAuto]);

    // Countdown pós-operação: a Binance leva alguns segundos pra refletir posição/saldo. Mostra um timer e
    // re-busca a cada 3s (sem F5), até zerar.
    useEffect(() => {
        if (syncSecs <= 0) return;
        const t = setTimeout(() => {
            const next = syncSecs - 1;
            setSyncSecs(next);
            if (next % 3 === 0) reload();
        }, 1000);
        return () => clearTimeout(t);
    }, [syncSecs, reload]);

    const saveAuto = useCallback((patch) => {
        setSavingAuto(true);
        return setEarnAutoConfig(patch)
            .then(() => loadAuto())
            .catch(err => setMsg({ ok: false, text: err.response?.data?.error || 'Falha ao salvar a automação.' }))
            .finally(() => setSavingAuto(false));
    }, [loadAuto]);

    if (loading || !data) return null;

    if (!data.available) {
        return (
            <div className='card card-body border-0 shadow mb-4'>
                <h6 className='fw-bold mb-1'>💰 Rendimento do caixa <span className='badge bg-secondary ms-1'>Earn</span></h6>
                <small className='text-muted'>Rendimento (Earn) {friendlyReason(data.reason)}</small>
            </div>
        );
    }

    const pos = data.position || { flexible: [], locked: [] };
    const posFlex = pos.flexible || [];
    const posLock = pos.locked || [];
    const inEarn = [...posFlex, ...posLock].reduce((s, p) => s + (Number(p.amount) || 0), 0);
    const accrued = [...posFlex, ...posLock].reduce((s, p) => s + (Number(p.accrued) || 0), 0);
    const flexProd = (data.flexible || []).find(p => p.canPurchase) || (data.flexible || [])[0];
    const locked = (data.locked || []).filter(p => p.canPurchase).slice(0, 4);
    const flexPosTotal = posFlex.reduce((s, p) => s + (+p.amount || 0), 0);

    function openAction(mode, productId, apr, min) {
        setAction({ mode, productId, apr, min: min || 0 });
        setAmountInput('');
        setMsg(null);
    }

    function confirmAction() {
        const amt = parseFloat(amountInput);
        const all = action.mode === 'redeem' && amountInput.trim() === '';
        if (action.mode === 'subscribe' && !(amt > 0)) { setMsg({ ok: false, text: 'Informe um valor maior que zero.' }); return; }
        if (action.mode === 'subscribe' && action.min && amt < action.min) { setMsg({ ok: false, text: `Mínimo do produto: ${fmt(action.min)} ${asset}.` }); return; }
        if (action.mode === 'redeem' && !all && !(amt > 0)) { setMsg({ ok: false, text: 'Informe um valor (ou deixe vazio para resgatar tudo).' }); return; }
        setBusy(true);
        const call = action.mode === 'subscribe'
            ? subscribeEarnFlexible(action.productId, amt, asset)
            : redeemEarnFlexible(action.productId, all ? 0 : amt, all);
        call
            .then(() => {
                setMsg({ ok: true, text: action.mode === 'subscribe' ? 'Aplicado com sucesso.' : 'Resgate solicitado.' });
                setAction(null);
                // A Binance leva alguns segundos pra refletir a posição/saldo → re-busca agora + countdown
                // visível com re-buscas (sem F5). Não bloqueia o botão.
                reload();
                setSyncSecs(12);
            })
            .catch(err => setMsg({ ok: false, text: err.response?.data?.error || 'Falha na operação.' }))
            .finally(() => setBusy(false));
    }

    return (
        <div className='card card-body border-0 shadow mb-4'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
                <h6 className='fw-bold mb-0'>💰 Rendimento do caixa <span className='badge bg-secondary ms-1'>Earn · {asset}</span></h6>
                <small className='text-muted'>flexível · resgate ~imediato</small>
            </div>

            {msg && <div className={`alert ${msg.ok ? 'alert-success' : 'alert-danger'} py-2`} style={{ fontSize: 13 }}>{msg.text}</div>}

            {syncSecs > 0 && (
                <div className='alert alert-info py-2 d-flex align-items-center gap-2' style={{ fontSize: 13 }}>
                    <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                    <span>Sincronizando com a Binance… o valor atualiza em <strong>{syncSecs}s</strong>.</span>
                </div>
            )}

            {inEarn > 0 ? (
                <div className='alert alert-success py-2 mb-3' style={{ fontSize: 13 }}>
                    Você tem <strong>{fmt(inEarn)} {asset}</strong> rendendo · juros acumulados <strong>{fmt(accrued)} {asset}</strong>.
                    {posFlex.length > 0 && (
                        <div className='d-flex justify-content-between align-items-center text-muted mt-1'>
                            <span>Flexível: {fmt(flexPosTotal)} {asset} · 🟢 resgate ~imediato · {pct(posFlex[0]?.apr)} a.a.</span>
                            <button className='btn btn-sm btn-outline-secondary' disabled={busy}
                                onClick={() => openAction('redeem', posFlex[0].productId, posFlex[0].apr)}>Resgatar</button>
                        </div>
                    )}
                    {posLock.map((p, i) => (
                        <span key={i} className='d-block text-muted'>Travado: {fmt(p.amount)} {asset} · 🔒 liberado em {p.redeemDate || '—'} · {pct(p.apr)} a.a.</span>
                    ))}
                </div>
            ) : (
                <small className='text-muted d-block mb-2'>Seu {asset} ocioso pode render — nada aplicado ainda.</small>
            )}

            {/* Formulário inline de confirmação (manual-confirm) */}
            {action && (
                <div className='border rounded p-3 mb-3 bg-light'>
                    <div className='fw-bold mb-1'>{action.mode === 'subscribe' ? `Aplicar no Flexível (${pct(action.apr)} a.a.)` : 'Resgatar do Flexível'}</div>
                    <div className='input-group input-group-sm mb-2' style={{ maxWidth: 320 }}>
                        <input type='number' className='form-control' min='0' step='any'
                            placeholder={action.mode === 'redeem' ? 'Valor (vazio = tudo)' : `Valor (mín. ${fmt(action.min)})`}
                            value={amountInput} onChange={e => setAmountInput(e.target.value)} />
                        <span className='input-group-text'>{asset}</span>
                    </div>
                    <small className='text-muted d-block mb-2'>
                        {action.mode === 'subscribe'
                            ? 'Sai do seu saldo livre (a reserva), nunca do capital das estratégias. APR variável, não garantido. Resgate fica ~imediato.'
                            : 'Resgate flexível é creditado ~na hora no saldo livre.'}
                    </small>
                    <div className='d-flex gap-2'>
                        <button className='btn btn-sm btn-primary' disabled={busy} onClick={confirmAction}>
                            {busy ? 'enviando...' : 'Confirmar'}
                        </button>
                        <button className='btn btn-sm btn-secondary' disabled={busy} onClick={() => setAction(null)}>Cancelar</button>
                    </div>
                </div>
            )}

            <div className='row g-2'>
                {flexProd && (
                    <div className='col-md-6'>
                        <div className='border rounded p-2 h-100 d-flex flex-column'>
                            <div className='fw-bold'>Flexível <span className='text-success'>{pct(flexProd.apr)} a.a.</span></div>
                            <small className='text-muted d-block mb-2'>🟢 Resgate rápido (~imediato). Mín. {fmt(flexProd.minPurchase)} {asset}. APR variável, não garantido.</small>
                            <button className='btn btn-sm btn-success mt-auto align-self-start' disabled={busy || flexProd.canPurchase === false}
                                onClick={() => openAction('subscribe', flexProd.productId, flexProd.apr, flexProd.minPurchase)}>Aplicar</button>
                        </div>
                    </div>
                )}
                {locked.map((p, i) => (
                    <div className='col-md-6' key={i}>
                        <div className='border rounded p-2 h-100'>
                            <div className='fw-bold'>Travado {p.durationDays}d <span className='text-success'>{pct(p.apr)} a.a.</span></div>
                            <small className='text-muted d-block'>🔒 TRAVADO por {p.durationDays} dias (sem resgate antes). Mín. {fmt(p.minPurchase)} {asset}. <em>(aplicar travado vem depois)</em></small>
                        </div>
                    </div>
                ))}
            </div>
            {/* Fase 3 — auto-keeper: render o caixa ocioso sozinho (buffer-keeper, nunca o capital das estratégias) */}
            <div className='border rounded p-3 mt-3 bg-light'>
                <div className='form-check form-switch mb-1'>
                    <input className='form-check-input' type='checkbox' role='switch' id='earnAutoSwitch'
                        checked={!!(auto && auto.enabled)} disabled={savingAuto}
                        onChange={e => saveAuto({ enabled: e.target.checked })} />
                    <label className='form-check-label fw-bold' htmlFor='earnAutoSwitch'>
                        ⚙️ Render o caixa ocioso automaticamente
                    </label>
                </div>
                <small className='text-muted d-block mb-2'>
                    Aplica sozinho o {asset} <strong>ocioso da reserva</strong> no Flexível e resgata quando o saldo livre
                    precisa de liquidez. Mantém líquido o que as estratégias ainda podem usar + seu colchão abaixo —
                    <strong> nunca toca o capital das estratégias</strong>.
                </small>

                <div className='d-flex align-items-center gap-2 mb-2'>
                    <label className='small text-muted mb-0'>Colchão de liquidez extra a manter livre:</label>
                    <div className='input-group input-group-sm' style={{ maxWidth: 180 }}>
                        <input type='number' className='form-control' min='0' step='any' placeholder='0'
                            value={keepInput} disabled={savingAuto}
                            onChange={e => setKeepInput(e.target.value)}
                            onBlur={() => { if (String(auto?.keepFreeUSDT ?? '') !== keepInput.trim()) saveAuto({ keepFreeUSDT: keepInput.trim() === '' ? null : keepInput.trim() }); }} />
                        <span className='input-group-text'>{asset}</span>
                    </div>
                </div>

                {auto && auto.preview && (
                    auto.preview.error ? (
                        <small className='text-muted d-block'>Prévia indisponível: {friendlyReason(auto.preview.error)}</small>
                    ) : auto.preview.action === 'park' ? (
                        <small className='d-block'>📥 Agora aplicaria <strong>~{fmt(auto.preview.amount)} {asset}</strong> (excedente da reserva). Piso de liquidez: {fmt(auto.preview.floor)} {asset}.</small>
                    ) : auto.preview.action === 'redeem' ? (
                        <small className='d-block'>📤 Agora resgataria <strong>~{fmt(auto.preview.amount)} {asset}</strong> (repor liquidez). Piso: {fmt(auto.preview.floor)} {asset}.</small>
                    ) : (
                        <small className='text-muted d-block'>✅ Em equilíbrio — nada a fazer agora (livre {fmt(auto.preview.free)} {asset} · no Earn {fmt(auto.preview.earnParked)} {asset}).</small>
                    )
                )}

                {auto && auto.enabled && auto.lastAction && (
                    <small className='text-muted d-block mt-1' style={{ fontSize: 12 }}>
                        🤖 Última ação automática:{' '}
                        {auto.lastAction.action === 'park' && auto.lastAction.ok
                            ? <>aplicou <strong>{fmt(auto.lastAction.amount)} {asset}</strong></>
                            : auto.lastAction.action === 'redeem' && auto.lastAction.ok
                                ? <>resgatou <strong>{fmt(auto.lastAction.amount)} {asset}</strong></>
                                : auto.lastAction.ok === false
                                    ? 'tentou operar mas a corretora não confirmou'
                                    : 'verificou, nada a fazer'}
                        {' · '}{ago(auto.lastAction.ts)}.
                    </small>
                )}
            </div>

            <small className='text-muted d-block mt-2'>APR e prazos vêm ao vivo da Binance — sempre da reserva, nunca do capital das estratégias.</small>
        </div>
    );
}

export default EarnPanel;
